import { useEffect, useRef, useCallback } from 'react';
import { supabase } from '@/integrations/supabase/client';

// Circuit breaker configuration
const CIRCUIT_BREAKER_CONFIG = {
  failureThreshold: 3,
  baseIntervalMs: 5 * 60 * 1000,
  maxIntervalMs: 15 * 60 * 1000,
  // Relaxed: avoid aggressive timeouts that surface false negatives
  operationTimeoutMs: 30000,
  visibilityCheckThresholdMs: 2 * 60 * 1000,
};

// Circuit breaker states
type CircuitState = 'closed' | 'open' | 'half-open';

/**
 * Wraps a promise with a timeout
 */
const withTimeout = <T>(
  promise: Promise<T>,
  timeoutMs: number,
  operationName: string
): Promise<T> => {
  return Promise.race([
    promise,
    new Promise<T>((_, reject) => {
      setTimeout(() => {
        reject(new Error(`Timeout: ${operationName} exceeded ${timeoutMs}ms`));
      }, timeoutMs);
    }),
  ]);
};

/**
 * Monitors database connection health and refreshes session periodically
 * to prevent idle connection issues.
 * 
 * Implements a circuit breaker pattern to:
 * - Stop hammering the database when it's unreachable
 * - Automatically retry with exponential backoff
 * - Gracefully recover when connection is restored
 * 
 * @param enabled - Whether to enable health monitoring (default: true)
 * @param intervalMs - Base interval for health checks (default: 5 minutes)
 */
export const useConnectionHealth = (enabled: boolean = true, intervalMs: number = CIRCUIT_BREAKER_CONFIG.baseIntervalMs) => {
  const timeoutRef = useRef<NodeJS.Timeout | null>(null);
  const lastActivityRef = useRef<number>(Date.now());
  const isCheckingRef = useRef<boolean>(false);
  
  // Circuit breaker state
  const circuitStateRef = useRef<CircuitState>('closed');
  const consecutiveFailuresRef = useRef<number>(0);
  const lastFailureTimeRef = useRef<number>(0);

  /**
   * Calculates the next check interval based on circuit state
   */
  const getNextInterval = useCallback((): number => {
    const { failureThreshold, baseIntervalMs, maxIntervalMs } = CIRCUIT_BREAKER_CONFIG;
    
    if (circuitStateRef.current === 'closed') {
      return intervalMs || baseIntervalMs;
    }
    
    // Exponential backoff when circuit is open
    const backoffMultiplier = Math.pow(2, Math.min(consecutiveFailuresRef.current - failureThreshold, 4));
    return Math.min(baseIntervalMs * backoffMultiplier, maxIntervalMs);
  }, [intervalMs]);

  /**
   * Records a successful health check
   */
  const recordSuccess = useCallback(() => {
    consecutiveFailuresRef.current = 0;
    circuitStateRef.current = 'closed';
    lastActivityRef.current = Date.now();
    console.log('[ConnectionHealth] Circuit closed - connection healthy');
  }, []);

  /**
   * Records a failed health check
   */
  const recordFailure = useCallback(() => {
    consecutiveFailuresRef.current++;
    lastFailureTimeRef.current = Date.now();
    
    const { failureThreshold } = CIRCUIT_BREAKER_CONFIG;
    
    if (consecutiveFailuresRef.current >= failureThreshold) {
      if (circuitStateRef.current !== 'open') {
        console.warn(`[ConnectionHealth] Circuit opened after ${consecutiveFailuresRef.current} failures`);
        circuitStateRef.current = 'open';
      }
    }
  }, []);

  /**
   * Schedules the next health check
   */
  const scheduleNextCheck = useCallback((performHealthCheck: () => Promise<void>) => {
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
    }
    
    const nextInterval = getNextInterval();
    console.log(`[ConnectionHealth] Next check in ${Math.round(nextInterval / 1000)}s (circuit: ${circuitStateRef.current})`);
    
    timeoutRef.current = setTimeout(performHealthCheck, nextInterval);
  }, [getNextInterval]);
  
  useEffect(() => {
    if (!enabled || typeof window === 'undefined') {
      return;
    }
    
    /**
     * Performs a health check with circuit breaker protection
     */
  const performHealthCheck = async () => {
      // Deduplication - skip if already checking
      if (isCheckingRef.current) {
        console.log('[ConnectionHealth] Skipping - check already in progress');
        return;
      }
      
      // If circuit is open, transition to half-open for a test
      if (circuitStateRef.current === 'open') {
        circuitStateRef.current = 'half-open';
        console.log('[ConnectionHealth] Circuit half-open - testing connection');
      }
      
      isCheckingRef.current = true;
      
      try {
        // Get session (no hard timeout to avoid false disconnects)
        const { data: { session }, error } = await supabase.auth.getSession();
        if (error) {
          console.warn('[ConnectionHealth] Session check failed:', error);
          recordFailure();
          scheduleNextCheck(performHealthCheck);
          return;
        }
        
        if (!session) {
          // No session to maintain - still counts as success (not a connection failure)
          recordSuccess();
          scheduleNextCheck(performHealthCheck);
          return;
        }
        
        // Check if session is close to expiring
        const expiresAt = session.expires_at;
        if (expiresAt) {
          const expiresIn = expiresAt * 1000 - Date.now();
          const refreshThreshold = 10 * 60 * 1000; // 10 minutes
          
          if (expiresIn < refreshThreshold) {
            console.log('[ConnectionHealth] Refreshing session proactively');
            try {
              await withTimeout(
                supabase.auth.refreshSession(),
                CIRCUIT_BREAKER_CONFIG.operationTimeoutMs,
                'refreshSession'
              );
            } catch (refreshError) {
              console.warn('[ConnectionHealth] Session refresh failed:', refreshError);
              // Don't count refresh failure as connection failure if we got the session
            }
          }
        }
        
        // Perform a lightweight query to keep connection alive with timeout
        // Skip extra health query to avoid additional timeouts
        recordSuccess();
      } catch (error) {
        console.error('[ConnectionHealth] Health check error:', error);
        recordFailure();
      } finally {
        isCheckingRef.current = false;
        scheduleNextCheck(performHealthCheck);
      }
    };
    
    // Perform initial health check with a small delay to avoid startup congestion
    const initialTimeout = setTimeout(performHealthCheck, 1000);
    
    // Handle visibility change - check when tab becomes visible after being hidden
    const handleVisibilityChange = () => {
      if (document.visibilityState === 'visible') {
        const timeSinceLastActivity = Date.now() - lastActivityRef.current;
        if (timeSinceLastActivity > CIRCUIT_BREAKER_CONFIG.visibilityCheckThresholdMs) {
          // Only check if not already checking and circuit isn't fully open
          if (!isCheckingRef.current) {
            console.log('[ConnectionHealth] Tab visible after inactivity - checking connection');
            performHealthCheck();
          }
        }
      }
    };
    
    // Handle focus - check when window gains focus after inactivity
    const handleFocus = () => {
      const timeSinceLastActivity = Date.now() - lastActivityRef.current;
      if (timeSinceLastActivity > CIRCUIT_BREAKER_CONFIG.visibilityCheckThresholdMs) {
        if (!isCheckingRef.current) {
          console.log('[ConnectionHealth] Window focused after inactivity - checking connection');
          performHealthCheck();
        }
      }
    };
    
    document.addEventListener('visibilitychange', handleVisibilityChange);
    window.addEventListener('focus', handleFocus);
    
    return () => {
      clearTimeout(initialTimeout);
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }
      document.removeEventListener('visibilitychange', handleVisibilityChange);
      window.removeEventListener('focus', handleFocus);
    };
  }, [enabled, intervalMs, recordSuccess, recordFailure, scheduleNextCheck]);
};






